/**
 * KontaktPage
 *
 * Contact form (codepilot-api /api/v1/contact/) + newsletter inline.
 * Per Codepilot Spec Post #827 / #828.
 *
 * Honeypot + time-trap anti-spam (no Captcha needed for typical bot
 * volumes). The honeypot field is invisible to humans; if it's filled,
 * we silently accept-and-drop. The time-trap rejects submissions that
 * happen <1.5s after the form mounted (humans can't fill a 5-field
 * form that fast).
 *
 * CORS + partner row are live since 2026-07-01 (codepilot-api PR #9,
 * commits 4ea42cc + d83718e). Submissions go straight to the API;
 * `mailtoFallback()` stays for the sidebar "reach me via email"
 * shortcut and as an error-recovery hint if fetch ever fails.
 */

import { useEffect, useRef, useState, FormEvent } from 'react'
import { Seo } from '@/components/Seo'
import { useT, useLanguage } from '@/context/LanguageContext'
import './ContentPage.css'
import './KontaktPage.css'

const FALLBACK_EMAIL = 'anna@sachersantana.com'

const STRINGS_PAGE = {
  title:       { de: 'Kontakt',           en: 'Contact' },
  intro: {
    de: 'Schreiben Sie mir gerne — sei es zu einer Skulptur, einem Atelierbesuch oder einer Anfrage. Ich melde mich persönlich zurück.',
    en: 'I would be glad to hear from you — about a sculpture, a studio visit, or any inquiry. I will reply personally.',
  },
  nameLabel:   { de: 'Name',              en: 'Name' },
  emailLabel:  { de: 'E-Mail',            en: 'Email' },
  subjectLabel:{ de: 'Betreff',           en: 'Subject' },
  msgLabel:    { de: 'Nachricht',         en: 'Message' },
  submit:      { de: 'Nachricht senden',  en: 'Send message' },
  sending:     { de: 'Wird gesendet…',    en: 'Sending…' },
  success: {
    de: 'Vielen Dank — Ihre Nachricht ist angekommen. Ich melde mich in den nächsten Tagen.',
    en: 'Thank you — your message has arrived. I will be in touch in the next few days.',
  },
  errorGeneric: {
    de: 'Das Senden hat nicht funktioniert. Bitte schreiben Sie mir direkt an',
    en: 'Sending failed. Please write to me directly at',
  },
  newsletterTitle: { de: 'Newsletter',    en: 'Newsletter' },
  newsletterIntro: {
    de: 'Ein bis zweimal im Jahr ein Brief zu neuen Werken, Ausstellungen und Atelierbesuchen — nicht öfter.',
    en: 'One or two letters a year about new works, exhibitions and studio visits — never more.',
  },
  newsletterCta: { de: 'Anmelden',        en: 'Subscribe' },
  newsletterSoon: {
    de: 'Newsletter-Anmeldung wird gerade vorbereitet. Bitte schreiben Sie mir bis dahin direkt — ich nehme Sie gerne in die Liste auf.',
    en: 'Newsletter sign-up is being set up. In the meantime please write to me directly — I will gladly add you to the list.',
  },
  phoneTitle: { de: 'Telefon',            en: 'Phone' },
  phoneNote: {
    de: 'Atelier am Wörthersee — bitte unter folgender E-Mail kurz Bescheid geben, ich rufe gern zurück:',
    en: 'Studio at Lake Wörthersee — please send a short email and I will be happy to call you back:',
  },
}

export function KontaktPage() {
  const t = useT()
  const { lang } = useLanguage()
  const formMountTimeRef = useRef(Date.now())
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset mount-time when the page becomes visible again (back-button etc.)
  useEffect(() => {
    formMountTimeRef.current = Date.now()
  }, [])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    const form = e.currentTarget
    const data = new FormData(form)

    // Time-trap: bots fill forms in milliseconds.
    const elapsed = Date.now() - formMountTimeRef.current
    if (elapsed < 1500) {
      // Silently drop, look like success to the bot.
      setSuccess(true)
      return
    }

    // Honeypot: visible only to bots that grab every input.
    const honeypot = data.get('website') as string
    if (honeypot) {
      setSuccess(true)
      return
    }

    const payload = {
      partner_id: 'annasacher',
      name: (data.get('name') as string)?.trim(),
      email: (data.get('email') as string)?.trim(),
      subject: (data.get('subject') as string)?.trim() || (lang === 'en' ? 'Inquiry' : 'Anfrage'),
      message: (data.get('message') as string)?.trim(),
    }

    setSubmitting(true)
    try {
      const res = await fetch('https://codepilot-api.arkserver.arkturian.com/api/v1/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setSuccess(true)
      form.reset()
    } catch (err) {
      setError(`${t(STRINGS_PAGE.errorGeneric)} ${FALLBACK_EMAIL}`)
    } finally {
      setSubmitting(false)
    }
  }

  function mailtoFallback() {
    const subj = encodeURIComponent(lang === 'en' ? 'Inquiry via sachersantana.com' : 'Anfrage über sachersantana.com')
    return `mailto:${FALLBACK_EMAIL}?subject=${subj}`
  }

  return (
    <article className="content-page">
      <Seo
        title={t(STRINGS_PAGE.title)}
        description={t(STRINGS_PAGE.intro)}
      />
      <div className="content-container content-container-prose">
        <header className="content-header content-header-prose">
          <h1>{t(STRINGS_PAGE.title)}</h1>
        </header>

        <p className="kontakt-intro">{t(STRINGS_PAGE.intro)}</p>

        {success ? (
          <div className="kontakt-success">
            <p>{t(STRINGS_PAGE.success)}</p>
          </div>
        ) : (
          <form className="kontakt-form" onSubmit={handleSubmit}>
            <label className="kontakt-field">
              <span>{t(STRINGS_PAGE.nameLabel)}</span>
              <input type="text" name="name" required autoComplete="name" />
            </label>

            <label className="kontakt-field">
              <span>{t(STRINGS_PAGE.emailLabel)}</span>
              <input type="email" name="email" required autoComplete="email" />
            </label>

            <label className="kontakt-field">
              <span>{t(STRINGS_PAGE.subjectLabel)}</span>
              <input type="text" name="subject" />
            </label>

            <label className="kontakt-field">
              <span>{t(STRINGS_PAGE.msgLabel)}</span>
              <textarea name="message" required minLength={10} rows={6} />
            </label>

            {/* Honeypot — hidden via CSS, only bots will fill it */}
            <label className="kontakt-honeypot" aria-hidden="true">
              Website
              <input type="text" name="website" tabIndex={-1} autoComplete="off" />
            </label>

            <button type="submit" className="kontakt-submit" disabled={submitting}>
              {submitting ? t(STRINGS_PAGE.sending) : t(STRINGS_PAGE.submit)}
            </button>

            {error && (
              <p className="kontakt-error">
                {error}
                <a href={mailtoFallback()}>{FALLBACK_EMAIL}</a>
              </p>
            )}
          </form>
        )}

        <hr className="kontakt-divider" />

        <section className="kontakt-newsletter">
          <h2>{t(STRINGS_PAGE.newsletterTitle)}</h2>
          <p>{t(STRINGS_PAGE.newsletterIntro)}</p>
          <p className="kontakt-newsletter-soon">{t(STRINGS_PAGE.newsletterSoon)}</p>
        </section>

        <section className="kontakt-phone">
          <h2>{t(STRINGS_PAGE.phoneTitle)}</h2>
          <p>{t(STRINGS_PAGE.phoneNote)}</p>
          <p>
            <a className="kontakt-mail" href={mailtoFallback()}>{FALLBACK_EMAIL}</a>
          </p>
        </section>
      </div>
    </article>
  )
}
